import { useState } from "react";
import { SERVICE_GRID } from "../../../../../constants/serviceGrid";
import DeleteIcon from "../../../../../assets/icons/delete.svg";
import CustomInput from "../../../components/CustomInput";
import { formatCurrency } from "../../utils/money";
import AdditionalFeeRow from "./AdditionalFeeRow";
import AddTierButton from "./AddTierButton";
import EditButton from "./EditButton";
import TierRow from "./TierRow";

const QuantityControl = ({
  value,
  editable,
  editing,
  onEdit,
  onChange,
  onCommit,
}) =>
  editing ? (
    <div className="mx-auto w-[70px]">
      <CustomInput
        value={(value || 1).toString()}
        onChange={onChange}
        height="36px"
        classNames="text-center"
        autoFocus
        onBlur={onCommit}
        onKeyDown={(event) => {
          if (
            !/[0-9]/.test(event.key) &&
            ![
              "Backspace",
              "Delete",
              "ArrowLeft",
              "ArrowRight",
              "Tab",
              "Enter",
            ].includes(event.key)
          ) {
            event.preventDefault();
          }
          if (event.key === "Enter") onCommit();
        }}
      />
    </div>
  ) : (
    <div className="flex items-center justify-center gap-1">
      <span>{value || 1}</span>
      {editable && <EditButton onClick={onEdit} ariaLabel="Sửa số lượng" />}
    </div>
  );

const ServiceCard = ({ service, index, data }) => {
  const [editingQuantity, setEditingQuantity] = useState(false);
  const [editingVat, setEditingVat] = useState(false);
  const [editingLicenseFeeId, setEditingLicenseFeeId] = useState(null);

  const {
    customPricing,
    visibleTiers,
    tempTierQuantities,
    showNextTier,
    removeTier,
    handleTierQuantityChange,
    removeService,
    updateCustomServiceName,
    updateCustomPrice,
    updateLicenseFee,
    updateCustomTierName,
    updateCustomTierPrice,
    updateCustomTierLicenseFee,
    updateQuantity,
    normalizeQuantity,
    updateAdditionalFeeName,
    updateAdditionalFeePrice,
    updateAdditionalFeeQuantity,
    showAdditionalFee,
    hideAdditionalFee,
    editingServiceNameId,
    setEditingServiceNameId,
    editingPriceId,
    setEditingPriceId,
    editingTierNameId,
    setEditingTierNameId,
    editingTierPriceId,
    setEditingTierPriceId,
    editingTierLicenseFeeId,
    setEditingTierLicenseFeeId,
    editingAdditionalFeeNameId,
    setEditingAdditionalFeeNameId,
    editingAdditionalFeePriceId,
    setEditingAdditionalFeePriceId,
    updateTierVat,
    updateAdditionalFeeVat,
  } = data;

  const price = Number(service.displayPrice) || 0;
  const licenseFee = Number(service.licenseFee) || 0;
  const quantity = Number(service.quantity) || 1;
  const currentVisibleTiers = visibleTiers[service.id] || [0];
  const otherVisibleTiers = currentVisibleTiers
    .filter((idx) => idx !== 0)
    .map((idx) => ({ tierIndex: idx, tier: service.priceTiers?.[idx] }))
    .filter((item) => item.tier);
  const hasNextTier = service.priceTiers && currentVisibleTiers.length < service.priceTiers.length;
  const subtotal = (price + licenseFee) * quantity;
  const vat = Number(service.tierVats?.[0]) || 0;
  const vatAmount = Math.round((subtotal * vat) / 100);

  const commitQuantity = () => {
    normalizeQuantity(service.id);
    setEditingQuantity(false);
  };

  return (
    <div className="mb-4 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm last:mb-0">
      <div className={`grid ${SERVICE_GRID} items-center px-4 py-4 text-sm`}>
        <div className="col-start-1 row-start-1 text-center">{index + 1}</div>

        <div className="col-start-2 row-start-1 flex justify-center text-center">
          {!customPricing || editingServiceNameId !== service.id ? (
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-800">{service.customName}</span>
              {customPricing && (
                <EditButton
                  onClick={() => setEditingServiceNameId(service.id)}
                  ariaLabel="Sửa tên dịch vụ"
                />
              )}
            </div>
          ) : (
            <CustomInput
              type="text"
              value={service.customName || ""}
              onChange={(value) => updateCustomServiceName(service.id, value)}
              height="34px"
              autoFocus
              onBlur={() => setEditingServiceNameId(null)}
              onKeyDown={(event) => event.key === "Enter" && setEditingServiceNameId(null)}
            />
          )}
        </div>

        <div className="col-start-3 row-start-1">
          <QuantityControl
            value={service.quantity}
            editable={customPricing}
            editing={customPricing && editingQuantity}
            onEdit={() => setEditingQuantity(true)}
            onChange={(value) => updateQuantity(service.id, value)}
            onCommit={commitQuantity}
          />
        </div>

        <div className="col-start-5 row-start-1 flex items-center justify-center gap-2 text-gray-800">
          {!customPricing || editingLicenseFeeId !== service.id ? (
            <>
              <span>{formatCurrency(licenseFee)}</span>
              {customPricing && (
                <EditButton
                  onClick={() => setEditingLicenseFeeId(service.id)}
                  ariaLabel="Sửa phí cấp bằng"
                />
              )}
            </>
          ) : (
            <CustomInput
              type="number"
              value={service.licenseFee?.toString() || ""}
              onChange={(value) => updateLicenseFee(service.id, value)}
              height="36px"
              autoFocus
              onBlur={() => setEditingLicenseFeeId(null)}
              onKeyDown={(event) => event.key === "Enter" && setEditingLicenseFeeId(null)}
            />
          )}
        </div>

        <div className="col-start-4 row-start-1 flex items-center justify-center gap-2 text-gray-800">
          {!customPricing || editingPriceId !== service.id ? (
            <>
              <span>{formatCurrency(price)}</span>
              {customPricing && (
                <EditButton
                  onClick={() => setEditingPriceId(service.id)}
                  ariaLabel="Sửa phí đăng ký"
                />
              )}
            </>
          ) : (
            <CustomInput
              type="number"
              value={service.displayPrice?.toString() || ""}
              onChange={(value) => updateCustomPrice(service.id, value)}
              height="36px"
              autoFocus
              onBlur={() => setEditingPriceId(null)}
              onKeyDown={(event) => event.key === "Enter" && setEditingPriceId(null)}
            />
          )}
        </div>

        <div className="col-start-6 row-start-1 flex items-center justify-center">
          {customPricing && editingVat ? (
            <div className="w-[80%] max-w-[70px]">
            <CustomInput
              type="number"
              value={service.tierVats?.[0] !== undefined ? service.tierVats[0].toString() : "10"}
              onChange={(value) => updateTierVat(service.id, 0, value)}
              height="36px"
              classNames="text-center"
              placeholder="%"
              onBlur={() => {
                if (service.tierVats?.[0] === "") updateTierVat(service.id, 0, 0);
                setEditingVat(false);
              }}
            />
            </div>
          ) : (
            <div className="flex items-center justify-center gap-1">
              <span>{service.tierVats?.[0] ?? 10}</span>
              {customPricing && (
                <EditButton
                  onClick={() => setEditingVat(true)}
                  ariaLabel="Sửa VAT"
                />
              )}
            </div>
          )}
        </div>

        <div className="col-start-7 row-start-1 flex flex-col items-center text-center">
          <div className="font-semibold text-gray-800">{formatCurrency(subtotal + vatAmount)}</div>
          {vatAmount > 0 && (
            <div className="mt-1 text-[10px] text-gray-400">Thuế: {formatCurrency(vatAmount)}</div>
          )}
        </div>

        <button type="button" onClick={() => removeService(service.id)} className="col-start-8 row-start-1 flex justify-center">
          <img src={DeleteIcon} alt="Xóa" className="h-4 w-4" />
        </button>
      </div>

      {otherVisibleTiers.map(({ tierIndex, tier }) => (
        <TierRow
          key={tierIndex}
          service={service}
          tier={tier}
          tierIndex={tierIndex}
          customPricing={customPricing}
          tempTierQuantities={tempTierQuantities}
          editingTierNameId={editingTierNameId}
          setEditingTierNameId={setEditingTierNameId}
          editingTierPriceId={editingTierPriceId}
          setEditingTierPriceId={setEditingTierPriceId}
          editingTierLicenseFeeId={editingTierLicenseFeeId}
          setEditingTierLicenseFeeId={setEditingTierLicenseFeeId}
          handleTierQuantityChange={handleTierQuantityChange}
          updateCustomTierName={updateCustomTierName}
          updateCustomTierPrice={updateCustomTierPrice}
          updateCustomTierLicenseFee={updateCustomTierLicenseFee}
          updateTierVat={updateTierVat}
          removeTier={removeTier}
        />
      ))}

      {service.additionalFeeVisible && (
        <AdditionalFeeRow
          service={service}
          customPricing={customPricing}
          editingAdditionalFeeNameId={editingAdditionalFeeNameId}
          setEditingAdditionalFeeNameId={setEditingAdditionalFeeNameId}
          editingAdditionalFeePriceId={editingAdditionalFeePriceId}
          setEditingAdditionalFeePriceId={setEditingAdditionalFeePriceId}
          updateAdditionalFeeName={updateAdditionalFeeName}
          updateAdditionalFeePrice={updateAdditionalFeePrice}
          updateAdditionalFeeQuantity={updateAdditionalFeeQuantity}
          updateAdditionalFeeVat={updateAdditionalFeeVat}
          onRemove={() => hideAdditionalFee(service.id)}
        />
      )}

      <div className="flex flex-wrap gap-2 border-t border-gray-100 bg-gray-50/50 px-6 py-3">
        {hasNextTier && <AddTierButton onClick={() => showNextTier(service.id)} />}
        {!service.additionalFeeVisible && (
          <AddTierButton label="Thêm phí bổ sung" onClick={() => showAdditionalFee(service.id)} />
        )}
      </div>
    </div>
  );
};

export default ServiceCard;
